/**
 * EmptyEnvironmentModal - Confirmation modal for the Empty Environment action
 *
 * Fetches guard conditions from the API, displays impact summary,
 * and requires typing "DELETE ALL" to confirm.
 */

import { useState, useEffect, useCallback } from 'react';
import { fetchEnvironmentStatus, fetchEmptyEnvironmentJob } from '../../api';
import { Btn } from '../../shared/components/redesign';

// ---------- Styles (redesign tokens) ----------

const overlayStyle = {
  position: 'fixed',
  inset: 0,
  background: 'rgba(0, 0, 0, 0.72)',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  zIndex: 9999,
  fontFamily: 'var(--rd-font-sans)',
};

const modalStyle = {
  background: 'var(--rd-color-panel)',
  border: '1px solid var(--rd-color-line-strong)',
  borderRadius: 'var(--rd-radius-xl)',
  padding: '20px 24px',
  maxWidth: 520,
  width: '90%',
  boxShadow: '0 30px 60px -20px rgba(0, 0, 0, 0.6)',
  color: 'var(--rd-color-text)',
};

const headerStyle = {
  display: 'flex',
  alignItems: 'flex-start',
  justifyContent: 'space-between',
  marginBottom: 18,
  gap: 12,
};

const headerTextStyle = {
  display: 'flex',
  flexDirection: 'column',
  gap: 4,
};

const eyebrowStyle = {
  fontFamily: 'var(--rd-font-mono)',
  fontSize: 'var(--rd-type-eyebrow)',
  fontWeight: 500,
  letterSpacing: 'var(--rd-track-eyebrow)',
  textTransform: 'uppercase',
  color: 'var(--rd-color-text-subtle)',
};

const titleStyle = {
  margin: 0,
  fontFamily: 'var(--rd-font-display)',
  fontSize: 18,
  fontWeight: 600,
  letterSpacing: '-0.3px',
  color: 'var(--rd-color-text)',
};

const closeButtonStyle = {
  background: 'transparent',
  border: 'none',
  color: 'var(--rd-color-text-muted)',
  fontSize: 18,
  cursor: 'pointer',
  padding: '4px 8px',
  lineHeight: 1,
  borderRadius: 'var(--rd-radius-sm)',
};

const sectionLabelStyle = {
  fontFamily: 'var(--rd-font-mono)',
  fontSize: 'var(--rd-type-eyebrow)',
  fontWeight: 500,
  letterSpacing: 'var(--rd-track-eyebrow)',
  textTransform: 'uppercase',
  color: 'var(--rd-color-text-subtle)',
  marginBottom: 8,
};

const panelStyle = {
  background: 'var(--rd-color-panel-lo)',
  border: '1px solid var(--rd-color-line)',
  borderRadius: 'var(--rd-radius-md)',
  padding: '12px 14px',
  marginBottom: 18,
};

const guardRowStyle = {
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  padding: '4px 0',
  fontSize: 'var(--rd-type-body)',
  color: 'var(--rd-color-text-muted)',
};

const inputStyle = {
  width: '100%',
  padding: '8px 12px',
  height: 32,
  background: 'var(--rd-color-panel-lo)',
  border: '1px solid var(--rd-color-line-strong)',
  borderRadius: 'var(--rd-radius-md)',
  color: 'var(--rd-color-text)',
  fontFamily: 'var(--rd-font-mono)',
  fontSize: 'var(--rd-type-body-dense)',
  outline: 'none',
  boxSizing: 'border-box',
};

const footerStyle = {
  display: 'flex',
  gap: 8,
  justifyContent: 'flex-end',
  marginTop: 18,
};

// ---------- Helpers ----------

function formatDuration(minutes) {
  if (minutes == null) return 'unknown';
  if (minutes < 60) return `${Math.round(minutes)}m ago`;
  const h = Math.floor(minutes / 60);
  const m = Math.round(minutes % 60);
  return `${h}h ${m}m ago`;
}

function formatSeconds(s) {
  if (s == null || s <= 0) return '0:00';
  const m = Math.floor(s / 60);
  const sec = s % 60;
  return `${m}:${String(sec).padStart(2, '0')}`;
}

function RateLimitCountdown({ initialSeconds, forceOverride, onToggleOverride }) {
  const [remaining, setRemaining] = useState(Math.max(0, initialSeconds || 0));

  useEffect(() => {
    if (remaining <= 0) return;
    const interval = setInterval(() => {
      setRemaining((prev) => Math.max(0, prev - 1));
    }, 1000);
    return () => clearInterval(interval);
  }, [remaining > 0]);

  return (
    <div style={{ marginTop: 4, paddingLeft: 8, display: 'flex', alignItems: 'center', gap: 8 }}>
      <span style={{
        color: 'var(--rd-color-text-subtle)',
        fontSize: 'var(--rd-type-body-micro)',
        fontFamily: 'var(--rd-font-mono)',
      }}>
        {formatSeconds(remaining)} remaining
      </span>
      <button
        onClick={onToggleOverride}
        style={{
          background: forceOverride ? 'var(--rd-status-error-bg-hi)' : 'transparent',
          border: `1px solid ${forceOverride ? 'var(--rd-status-error-line)' : 'var(--rd-color-line-strong)'}`,
          borderRadius: 'var(--rd-radius-pill)',
          color: forceOverride ? 'var(--rd-status-error-fg)' : 'var(--rd-color-text-subtle)',
          fontSize: 'var(--rd-type-eyebrow)',
          letterSpacing: 0.4,
          textTransform: 'uppercase',
          padding: '2px 8px',
          cursor: 'pointer',
          fontFamily: 'var(--rd-font-sans)',
          fontWeight: 600,
        }}
      >
        {forceOverride ? 'Override active' : 'Override'}
      </button>
    </div>
  );
}

function GuardRow({ label, passed }) {
  return (
    <div style={guardRowStyle}>
      <span style={{ color: 'var(--rd-color-text)' }}>{label}</span>
      <span style={{
        color: passed ? 'var(--rd-status-done-fg)' : 'var(--rd-status-error-fg)',
        fontFamily: 'var(--rd-font-mono)',
        fontSize: 'var(--rd-type-eyebrow)',
        fontWeight: 600,
        letterSpacing: 0.4,
        textTransform: 'uppercase',
      }}>
        {passed ? 'Passed' : 'Failed'}
      </span>
    </div>
  );
}

// ---------- Job progress ----------

const POLL_MS = 2000;

function formatElapsed(s) {
  if (s == null || Number.isNaN(s)) return '';
  if (s < 60) return `${Math.round(s)}s`;
  return `${Math.floor(s / 60)}m ${Math.round(s % 60)}s`;
}

// What the running (or finished) job says, in plain words. The job itself runs
// in the operator-worker service, so closing this modal or reloading the page
// does not stop it; opening the modal again shows it here.
export function JobProgress({ job }) {
  const total = job?.total ?? 0;
  const done = job?.done ?? 0;
  const pct = total > 0 ? Math.min(100, Math.round((done / total) * 100)) : 0;
  const stale = !job?.live && (job?.state === 'queued' || job?.state === 'running');
  // Server clock only (heartbeat = last progress), so rendering stays pure.
  const elapsed = job?.started_at ? (job.finished_at || job.heartbeat || job.started_at) - job.started_at : null;

  let headline;
  if (job?.state === 'done') {
    headline = `Emptied ${(done - (job.skipped || 0)).toLocaleString()} photo${done === 1 ? '' : 's'} in ${formatElapsed(elapsed)}`;
  } else if (job?.state === 'failed') {
    headline = 'Emptying failed';
  } else if (stale) {
    headline = 'The worker stopped responding';
  } else if (job?.state === 'queued') {
    headline = 'Queued, waiting for the worker…';
  } else if (total === 0 && !job?.snapshot) {
    headline = 'Starting…';
  } else {
    headline = `Emptying ${done.toLocaleString()} / ${total.toLocaleString()} photos`;
  }

  return (
    <div style={panelStyle}>
      <div style={{
        color: job?.state === 'failed' || stale ? 'var(--rd-status-error-fg)' : 'var(--rd-color-text)',
        fontFamily: 'var(--rd-font-display)',
        fontSize: 15,
        fontWeight: 600,
        marginBottom: 8,
      }}>
        {headline}
      </div>
      {job?.state !== 'done' && job?.state !== 'failed' && !stale && (
        <div style={{ height: 6, background: 'var(--rd-color-border)', borderRadius: 3, overflow: 'hidden' }}>
          <div style={{ width: `${pct}%`, height: '100%', background: 'var(--rd-status-error-fg)', transition: 'width .4s' }} />
        </div>
      )}
      <div style={{
        color: 'var(--rd-color-text-muted)',
        fontFamily: 'var(--rd-font-mono)',
        fontSize: 'var(--rd-type-body-dense)',
        marginTop: 8,
      }}>
        {(job?.files_deleted ?? 0).toLocaleString()} files · {(job?.faces_deleted ?? 0).toLocaleString()} unlabeled faces removed
        {job?.leftover_dirs ? ` · ${job.leftover_dirs.toLocaleString()} leftover folders` : ''}
        {job?.skipped ? ` · ${job.skipped.toLocaleString()} skipped (archive failed, files kept)` : ''}
        {elapsed != null && job?.state !== 'done' ? ` · ${formatElapsed(elapsed)}` : ''}
      </div>
      {job?.office_deletes_queued != null && (
        // DC2 item 37: the office pixel copies are removed by smith at the
        // office. The job only queued them; say so, never imply they are gone.
        <div data-testid="empty-env-office" style={{
          color: 'var(--rd-color-text-muted)',
          fontFamily: 'var(--rd-font-mono)',
          fontSize: 'var(--rd-type-body-dense)',
          marginTop: 4,
        }}>
          {job.office_deletes_queued.toLocaleString()} office folders queued for removal (queued, not done yet)
          {job.office_queue_depth != null ? ` · ${job.office_queue_depth.toLocaleString()} waiting in the office queue` : ''}
        </div>
      )}
      {job?.office_deletes_failed > 0 && (
        <div data-testid="empty-env-office-failed" style={{ color: 'var(--rd-status-attention-fg)', fontSize: 'var(--rd-type-body-dense)', marginTop: 8 }}>
          {job.office_deletes_failed.toLocaleString()} office deletes could not be queued; the office removes them on its next catch-up (after it reconnects).
        </div>
      )}
      {job?.state === 'failed' && job?.error && (
        <div style={{ color: 'var(--rd-status-error-fg)', fontSize: 'var(--rd-type-body-dense)', marginTop: 8 }}>
          {job.error}. Press Empty Environment again to retry; photos already emptied are not touched again.
        </div>
      )}
      {stale && (
        <div style={{ color: 'var(--rd-status-error-fg)', fontSize: 'var(--rd-type-body-dense)', marginTop: 8 }}>
          No progress for over a minute. Press Empty Environment again to resume where it stopped.
        </div>
      )}
    </div>
  );
}

// ---------- Main component ----------

function EmptyEnvironmentModal({ isOpen, onClose, onConfirm, onFinished, stats }) {
  const [guardStatus, setGuardStatus] = useState(null);
  const [guardLoading, setGuardLoading] = useState(true);
  const [guardError, setGuardError] = useState(null);
  const [confirmText, setConfirmText] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState(null);
  const [job, setJob] = useState(null);

  // A job already running (another tab, before a reload) is shown straight away.
  useEffect(() => {
    let cancelled = false;
    fetchEmptyEnvironmentJob()
      .then((j) => { if (!cancelled && j && (j.live || j.state === 'failed')) setJob(j); })
      .catch(() => {});
    return () => { cancelled = true; };
  }, []);

  // Follow a live job until it finishes.
  const jobActive = job && job.live && (job.state === 'queued' || job.state === 'running');
  useEffect(() => {
    if (!jobActive) return undefined;
    const t = setInterval(() => {
      fetchEmptyEnvironmentJob()
        .then((j) => {
          if (!j) return;
          setJob(j);
          if (j.state === 'done') onFinished?.();
        })
        .catch(() => {});
    }, POLL_MS);
    return () => clearInterval(t);
  }, [jobActive, onFinished]);

  // Fetch guard conditions on mount (component is conditionally rendered)
  useEffect(() => {
    let cancelled = false;
    fetchEnvironmentStatus()
      .then((data) => {
        if (!cancelled) setGuardStatus(data);
      })
      .catch((err) => {
        console.error('Failed to fetch environment status:', err);
        if (!cancelled) setGuardError('Failed to check guard conditions');
      })
      .finally(() => {
        if (!cancelled) setGuardLoading(false);
      });
    return () => { cancelled = true; };
  }, []);

  const [forceOverride, setForceOverride] = useState(false);

  // Core guards (no live matches + cooldown expired) must always pass
  const coreGuardsPassed = guardStatus?.no_live_matches && guardStatus?.cooldown_expired;
  // Rate limit can be overridden when core guards pass
  const rateLimitPassed = guardStatus?.rate_limit_expired !== false;
  const allGuardsPassed = coreGuardsPassed && (rateLimitPassed || forceOverride);
  const confirmValid = confirmText === 'DELETE ALL';
  const jobRunning = !!(job && job.live && (job.state === 'queued' || job.state === 'running'));
  const jobDone = job?.state === 'done';
  const jobStale = !!(job && !job.live && (job.state === 'queued' || job.state === 'running'));
  const canSubmit = allGuardsPassed && confirmValid && !submitting && !jobRunning && !jobDone;

  const handleConfirm = useCallback(async () => {
    if (!canSubmit) return;
    setSubmitting(true);
    setSubmitError(null);
    try {
      const data = await onConfirm({ force: forceOverride });
      setJob(data?.job || null);
      setConfirmText('');
      setSubmitting(false);
    } catch (err) {
      if (err?.response?.status === 409 && err?.response?.data?.job) {
        setJob(err.response.data.job);  // already running: follow it
        setSubmitting(false);
        return;
      }
      setSubmitError(err?.response?.data?.error || err?.response?.data?.detail || 'Failed to empty environment');
      setSubmitting(false);
    }
  }, [canSubmit, onConfirm, forceOverride]);

  if (!isOpen) return null;

  // Build impact summary from stats
  const total = stats?.total ?? 0;
  const byStatus = stats?.by_status || {};
  const done = byStatus.done || 0;
  const exported = byStatus.exported || 0;
  const pending = total - done - exported;

  return (
    <div style={overlayStyle} onClick={onClose}>
      <div style={modalStyle} onClick={(e) => e.stopPropagation()}>
        {/* Header */}
        <div style={headerStyle}>
          <div style={headerTextStyle}>
            <span style={eyebrowStyle}>§ DESTRUCTIVE</span>
            <h3 style={titleStyle}>Empty Environment</h3>
          </div>
          <button style={closeButtonStyle} onClick={onClose} title="Close" aria-label="Close">
            ×
          </button>
        </div>

        {/* Job progress (queued / running / done / failed) */}
        {job && (
          <>
            <div style={sectionLabelStyle}>Progress</div>
            <JobProgress job={job} />
          </>
        )}

        {/* Guard Status */}
        <div style={sectionLabelStyle}>Guard Status</div>
        <div style={panelStyle}>
          {guardLoading && (
            <div style={{ color: '#888', fontSize: 14 }}>Checking conditions...</div>
          )}
          {guardError && (
            <div style={{ color: '#ff6b6b', fontSize: 14 }}>{guardError}</div>
          )}
          {guardStatus && !guardLoading && (
            <>
              <GuardRow
                label="No live matches"
                passed={guardStatus.no_live_matches}
              />
              <GuardRow
                label="Cooldown expired"
                passed={guardStatus.cooldown_expired}
              />
              {guardStatus.last_match_ended_minutes != null && (
                <div style={{ color: '#888', fontSize: 12, marginTop: 4, paddingLeft: 8 }}>
                  Last match ended: {formatDuration(guardStatus.last_match_ended_minutes)}
                </div>
              )}
              <GuardRow
                label="Rate limit"
                passed={guardStatus.rate_limit_expired !== false}
              />
              {guardStatus.rate_limit_expired === false && coreGuardsPassed && (
                <RateLimitCountdown
                  initialSeconds={guardStatus.rate_limit_remaining_seconds}
                  forceOverride={forceOverride}
                  onToggleOverride={() => setForceOverride(!forceOverride)}
                />
              )}
            </>
          )}
        </div>

        {/* Impact Summary */}
        <div style={sectionLabelStyle}>Impact Summary</div>
        <div style={panelStyle}>
          <div style={{
            color: 'var(--rd-status-error-fg)',
            fontFamily: 'var(--rd-font-display)',
            fontSize: 15,
            fontWeight: 600,
            marginBottom: 4,
          }}>
            {total.toLocaleString()} photo{total !== 1 ? 's' : ''} will be permanently deleted
          </div>
          <div style={{
            color: 'var(--rd-color-text-muted)',
            fontFamily: 'var(--rd-font-mono)',
            fontSize: 'var(--rd-type-body-dense)',
          }}>
            {done.toLocaleString()} done · {exported.toLocaleString()} exported · {pending.toLocaleString()} pending
          </div>
        </div>

        {/* Type-to-confirm */}
        <div style={{
          marginBottom: 6,
          color: 'var(--rd-color-text-muted)',
          fontSize: 'var(--rd-type-body)',
        }}>
          To confirm, type{' '}
          <strong style={{ color: 'var(--rd-color-text)', fontFamily: 'var(--rd-font-mono)' }}>
            DELETE ALL
          </strong>
          {' '}below:
        </div>
        <input
          style={inputStyle}
          value={confirmText}
          onChange={(e) => setConfirmText(e.target.value)}
          placeholder="Type DELETE ALL"
          autoFocus
          disabled={submitting}
        />

        {/* Error message */}
        {submitError && (
          <div style={{
            color: 'var(--rd-status-error-fg)',
            fontSize: 'var(--rd-type-body-dense)',
            marginTop: 8,
          }}>
            {submitError}
          </div>
        )}

        {/* Footer */}
        <div style={footerStyle}>
          <Btn kind="ghost" size="sm" onClick={onClose} disabled={submitting}>
            {jobRunning || jobDone ? 'Close' : 'Cancel'}
          </Btn>
          <Btn
            kind="danger"
            size="sm"
            onClick={handleConfirm}
            disabled={!canSubmit}
          >
            {submitting ? 'Queueing…' : jobRunning ? 'Emptying…' : jobStale ? 'Resume' : 'Empty Environment'}
          </Btn>
        </div>
      </div>
    </div>
  );
}

export default EmptyEnvironmentModal;
